import {
    FeatureState,
    isFeatureEnabled,
    toFeatureState,
} from '@etrigan/feature-toggles-client'
import { RendererMiddleware } from 'json-react-layouts'
import React, { PropsWithChildren } from 'react'
import { useToggleState } from './feature-togglings/features'
import { Features } from './features'
import { ComponentServices } from './routing/page-definition'

export type OptionalFeature = undefined | Features | [Features, boolean]

export function isComponentEnabled(
    featureState: FeatureState,
    feature: OptionalFeature,
) {
    if (typeof feature === 'string') {
        if (!isFeatureEnabled(featureState, feature)) {
            return false
        }
    } else if (Array.isArray(feature)) {
        const [featureName, flag] = feature
        if (isFeatureEnabled(featureState, featureName) !== flag) {
            return false
        }
    }

    return true
}

export function FeatureEnabled(
    props: PropsWithChildren<{ feature: Features | [Features, boolean] }>,
) {
    const isEnabled = isComponentEnabled(
        toFeatureState(useToggleState()),
        props.feature,
    )
    if (!isEnabled) {
        return null
    }

    return <React.Fragment>{props.children}</React.Fragment>
}

export const componentFeatureToggleMiddleware: RendererMiddleware<
    ComponentServices,
    {
        /**
         * The feature toggle, or the feature toggle with the state to match
         * @example feature: ['my-feature', true] - render if feature toggle is on
         * @example feature: ['my-feature', false] - render if feature toggle is off
         */
        feature?: Features | [Features, boolean]
    }
> = (componentProps, middlewareProps, services, next) => {
    if (middlewareProps.feature) {
        return (
            <FeatureEnabled feature={middlewareProps.feature}>
                {next(componentProps, middlewareProps, services)}
            </FeatureEnabled>
        )
    }

    return next(componentProps, middlewareProps, services)
}
export const componentOnEventMiddleware: RendererMiddleware<
    ComponentServices,
    {}
> = (componentProps, middlewareProps, serviceProps, next) => {
    const modifiedServices = {
        ...serviceProps,
        services: { ...serviceProps.services },
    }

    modifiedServices.services.onEvent = (event: any) => {
        serviceProps.services.onEvent({
            context: {
                timeStamp: Date.now(),
                componentType: componentProps.componentType,
                componentRenderPath: componentProps.componentRenderPath,
                dataDefinition: componentProps.dataDefinitionArgs,
            },
            ...event,
        })
    }

    return next(componentProps, middlewareProps, modifiedServices)
}
